import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api, { fmtBytes, fmtDate, fmtDuration } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["all", "queued", "running", "succeeded", "partially_failed", "failed", "cancelled"];

export default function Runs() {
  const { t } = useTranslation();
  const [runs, setRuns] = useState([]);
  const [status, setStatus] = useState("all");
  const navigate = useNavigate();

  const load = useCallback(() => {
    const params = status !== "all" ? `?status=${status}` : "";
    api.get(`/runs${params}`).then((r) => setRuns(r.data)).catch(() => toast.error(t("runs.loadFailed")));
  }, [status, t]);

  useEffect(() => {
    load();
    const tt = setInterval(load, 5000);
    return () => clearInterval(tt);
  }, [load]);

  return (
    <div data-testid="runs-page">
      <PageHeader title={t("runs.title")} subtitle={t("runs.subtitle")}
        actions={
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-none w-44" data-testid="runs-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`runs.filter.${s}`)}</SelectItem>)}</SelectContent>
          </Select>
        } />
      <div className="p-6 md:p-8">
        {runs.length === 0 ? (
          <div className="grid-panel p-16 text-center text-muted-foreground">{t("runs.noMatch")}</div>
        ) : (
          <div className="grid-panel overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                  <th className="px-5 py-3 font-medium">{t("runs.thRun")}</th>
                  <th className="px-5 py-3 font-medium">{t("runs.thPlan")}</th>
                  <th className="px-5 py-3 font-medium">{t("runs.thStatus")}</th>
                  <th className="px-5 py-3 font-medium">{t("runs.thPhase")}</th>
                  <th className="px-5 py-3 font-medium">{t("runs.thSize")}</th>
                  <th className="px-5 py-3 font-medium">{t("runs.thDuration")}</th>
                  <th className="px-5 py-3 font-medium">{t("runs.thStarted")}</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="data-grid-row cursor-pointer" onClick={() => navigate(`/runs/${r.id}`)} data-testid={`run-row-${r.id}`}>
                    <td className="px-5 py-3 font-mono text-xs">{r.id}</td>
                    <td className="px-5 py-3 font-medium">{r.plan_name || r.plan_id}</td>
                    <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{r.phase || "—"}</td>
                    <td className="px-5 py-3 font-mono text-xs">{fmtBytes(r.final_size)}</td>
                    <td className="px-5 py-3 font-mono text-xs">{fmtDuration(r.duration_seconds)}</td>
                    <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{fmtDate(r.started_at || r.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
